import type { Nullable } from "models-webgl2";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";
import { CanvasResizeSelectionComponent, Webgl2Component } from "../../webgl2-component";
import { Vec2 } from "utils/math/vec2";
import { CanvasResizeSelectionBottomComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-bottom-component";
import { CanvasResizeSelectionBottomLeftComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-bottom-left-component";
import { CanvasResizeSelectionBottomRightComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-bottom-right-component";
import { CanvasResizeSelectionLeftComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-left-component";
import { CanvasResizeSelectionRightComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-right-component";
import { CanvasResizeSelectionTopComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-top-component";
import { CanvasResizeSelectionTopLeftComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-top-left-component";
import { CanvasResizeSelectionTopRightComponent } from "../../webgl2-component/components/canvas-resize-component/canvas-resize-selection-top-right-component";

const DEFAULT_CURSOR = "default";
const CANVAS_RESIZE_CURSOR_AABB_MARGIN_PX = 40;
const CANVAS_RESIZE_AUTO_FIT_DELAY_MS = 3000;

export class CanvasResizeTool extends Webgl2Tool {
  private _currComponent: Nullable<Webgl2Component>;
  private _lastCursor: string = DEFAULT_CURSOR;

  private _pendingAutoFit = false;
  private _autoFitTimeoutId: number | null = null;
  private _disposeCanvasTransform: VoidFunction | null = null;
  private _disposeCameraChange: VoidFunction | null = null;
  private _isRunningAutoFit = false;

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();
  }

  public onAssign() {
    this._renderer.selectComponent(this._renderer.chessGridComponent);
    this._renderer.canvasResizeSelectionComponent.copyComponentTransform(
      this._renderer.chessGridComponent,
    );
    this._setCursor(DEFAULT_CURSOR);

    this._disposeCanvasTransform?.();
    this._disposeCanvasTransform = this._renderer.events.on("canvasTransform", () => {
      this._pendingAutoFit = true;
      this._scheduleAutoFit();
    });

    // Any camera interaction (wheel pan/zoom, trackpad, etc.) counts as "activity" and
    // should postpone the auto-fit while we have a pending resize adjustment.
    this._disposeCameraChange?.();
    this._disposeCameraChange = this._renderer.camera.events.on("change", () => {
      if (!this._pendingAutoFit || this._isRunningAutoFit) return;
      if (!this._autoFitTimeoutId) return;
      this._scheduleAutoFit();
    });
  }

  public onUnAssign() {
    this._renderer.selectComponent(null);
    this._setCursor(DEFAULT_CURSOR);

    this._clearAutoFitTimer();
    this._pendingAutoFit = false;

    this._disposeCanvasTransform?.();
    this._disposeCanvasTransform = null;

    this._disposeCameraChange?.();
    this._disposeCameraChange = null;
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    this._handleMove(e);
  }

  public onMouseOver(e: MouseEvent) {
    if (this._currComponent) {
      this._setCursor(this._getCursorByComponent(this._currComponent));
      return;
    }

    if (!this._isMousePossiblyOverResizeSelection(e)) {
      this._setCursor(DEFAULT_CURSOR);
      return;
    }

    const hovered = this._renderer.findComponentUnderMouse(e);
    const root = Webgl2dRenderer.global.getRootParent(hovered).slice(-1)[0];

    if (root instanceof CanvasResizeSelectionComponent) {
      this._setCursor(this._getCursorByComponent(hovered));
      return;
    }

    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseLeave() {
    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseEnded(e: MouseEvent) {
    return this._handleUp(e);
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    this._handleMove(e);
  }

  public onTouchEnded(e: TouchEvent) {
    return this._handleUp(e);
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    this._currComponent = this._renderer.findComponentUnderMouse(e);

    if (this._currComponent?.onMouseDown) {
      this._currComponent?.onMouseDown(e);
    } else {
      this._currComponent = null;
      this._renderer.tools.handTool.handleDown(e);
    }
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent.onMouseMove?.(e);
    } else {
      this._renderer.tools.handTool.handleMove(e);
    }
  }

  private _handleUp(e: MouseEvent | TouchEvent) {
    this._currComponent?.onMouseUp?.(e);
    this._currComponent = null;
  }

  private _setCursor(cursor: string) {
    if (cursor === this._lastCursor) return;
    this._lastCursor = cursor;
    this._renderer.canvas.style.cursor = cursor;
  }

  private _clearAutoFitTimer() {
    if (!this._autoFitTimeoutId) return;
    window.clearTimeout(this._autoFitTimeoutId);
    this._autoFitTimeoutId = null;
  }

  private _scheduleAutoFit() {
    this._clearAutoFitTimer();
    this._autoFitTimeoutId = window.setTimeout(() => {
      this._autoFitTimeoutId = null;
      if (!this._pendingAutoFit) return;

      if (!this._renderer.eventsManager.checkForTool(this._renderer.tools.canvasResizeTool)) {
        return;
      }

      // Don't auto-fit while user is still dragging/touching (even if they paused).
      if (this._renderer.eventsManager.isInMouseTouchEvent) {
        this._scheduleAutoFit();
        return;
      }

      this._pendingAutoFit = false;
      this._isRunningAutoFit = true;
      try {
        this._renderer.zoomToCenterSmooth();
      } finally {
        this._isRunningAutoFit = false;
      }
    }, CANVAS_RESIZE_AUTO_FIT_DELAY_MS);
  }

  private _isMousePossiblyOverResizeSelection(e: MouseEvent): boolean {
    try {
      const mouse = this._renderer.camera.getGlobalMousePosition(e);
      const sel = this._renderer.canvasResizeSelectionComponent;
      const tl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(sel, Vec2.zero());
      const tr = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(sel, Vec2.oneZero());
      const br = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(sel, Vec2.one());
      const bl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(sel, Vec2.zeroOne());

      const minX = Math.min(tl.x, tr.x, br.x, bl.x);
      const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
      const minY = Math.min(tl.y, tr.y, br.y, bl.y);
      const maxY = Math.max(tl.y, tr.y, br.y, bl.y);

      const scale = this._renderer.camera.scale || 1;
      const margin = CANVAS_RESIZE_CURSOR_AABB_MARGIN_PX / scale;

      return (
        mouse.x >= minX - margin &&
        mouse.x <= maxX + margin &&
        mouse.y >= minY - margin &&
        mouse.y <= maxY + margin
      );
    } catch {
      return true;
    }
  }

  private _getCursorByComponent(component: Nullable<Webgl2Component>): string {
    if (!component) return DEFAULT_CURSOR;

    if (
      component instanceof CanvasResizeSelectionTopLeftComponent ||
      component instanceof CanvasResizeSelectionBottomRightComponent
    ) {
      return "nwse-resize";
    }

    if (
      component instanceof CanvasResizeSelectionTopRightComponent ||
      component instanceof CanvasResizeSelectionBottomLeftComponent
    ) {
      return "nesw-resize";
    }

    if (
      component instanceof CanvasResizeSelectionTopComponent ||
      component instanceof CanvasResizeSelectionBottomComponent
    ) {
      return "ns-resize";
    }

    if (
      component instanceof CanvasResizeSelectionLeftComponent ||
      component instanceof CanvasResizeSelectionRightComponent
    ) {
      return "ew-resize";
    }

    if (component instanceof CanvasResizeSelectionComponent) {
      return "move";
    }

    return DEFAULT_CURSOR;
  }
}

export default CanvasResizeTool;
